import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../store/store";
import DashboardLayout from "./DashboardLayout";
import { collectEstimationDataCycleSave, saveSectionApiCall, fetchCycleData, type QualityWallRecordData } from "../Services/QualityWallRecord";
import { addOfflineSubmissionByCategory } from "../store/stateSlice.ts";

const QualityWallRecord: React.FC = () => {
    console.log('QualityWallRecord component is rendering...');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    // Redux state
    const plantTourId = useSelector((state: RootState) => state.planTour.plantTourId);
    const selectedCycle = useSelector((state: RootState) => state.planTour.selectedCycle);
    const user = useSelector((state: any) => state.user.user);
    const isOfflineStarted = useSelector((state: RootState) => state.appState.isOfflineStarted);
    const offlineSubmissionsByCategory = useSelector((state: RootState) => state.appState.offlineSubmissionsByCategory);
    
    // Local state
    const [currentCycle, setCurrentCycle] = useState(1);
    const [isSessionStarted, setIsSessionStarted] = useState(false);
    const [isExpanded, setIsExpanded] = useState<{ [key: number]: boolean }>({});
    const [expandedCompletedCycles, setExpandedCompletedCycles] = useState<Set<string>>(new Set());
    // const [isLoadingCycles, setIsLoadingCycles] = useState(false);
    const [completedCycles, setCompletedCycles] = useState<Set<number>>(new Set());
    const [cycleData, setCycleData] = useState<{ [key: number]: {
        productName: string;
        batchNo: string;
        // Last saved category for the cycle (kept for backward compatibility)
        category: string;
        lineNo: string;
        executiveName: string;
        sku: string;
        evaluations: Array<{
            membersPresent: string;
            packAppearance: string;
            sealingQuality: string;
            coding: string;
            rating: string;
            appearance?: string;
            color?: string;
            texture?: string;
            flavour?: string;
            taste?: string;
            remarksDefect?: string;
            // Per-row category so a single cycle can have both Pack and Product entries
            category?: string;
        }>;
    } }>({});
    
    // Evaluation entry type (supports both Pack Quality and Product Quality)
    type EvaluationEntry = {
        id: string;
        membersPresent: string;
        packAppearance: string;
        sealingQuality: string;
        coding: string;
        rating: string;
        appearance: string;
        color: string;
        texture: string;
        flavour: string;
        taste: string;
        remarksDefect: string;
    };

    const defaultEntry = (): EvaluationEntry => ({
        id: Date.now().toString(),
        membersPresent: '',
        packAppearance: '',
        sealingQuality: '',
        coding: '',
        rating: '',
        appearance: '',
        color: '',
        texture: '',
        flavour: '',
        taste: '',
        remarksDefect: ''
    });

    const initialTabbedEntries = (): Record<string, EvaluationEntry[]> => ({
        'Pack Quality': [defaultEntry()],
        'Product Quality': [defaultEntry()]
    });

    // Per-tab evaluation entries so switching tabs keeps each tab's data
    const [tabbedEvaluationEntries, setTabbedEvaluationEntries] = useState<Record<string, EvaluationEntry[]>>(initialTabbedEntries);

    // Start form data
    const [startFormData, setStartFormData] = useState({
        productName: '',
        batchNo: '',
        category: 'Pack Quality',
        lineNo: '',
        executiveName: '',
        sku: ''
    });

    const displayedEvaluationEntries = tabbedEvaluationEntries[startFormData.category] ?? [defaultEntry()];

    // Wall Summary rating column background by lowest attribute score: 0-1 GREEN, 2-3 YELLOW, 4 RED
    const getRatingCellStyle = (rating: string | undefined): React.CSSProperties => {
        const base = { textAlign: 'left' as const, padding: '12px 16px', fontSize: '14px', color: '#000000' };
        const num = rating !== undefined && rating !== '' ? parseInt(String(rating).trim(), 10) : null;
        if (num === null || isNaN(num)) return { ...base, backgroundColor: 'transparent' };
        if (num === 0 || num === 1) return { ...base, backgroundColor: '#dcfce7' }; // green
        if (num === 2 || num === 3) return { ...base, backgroundColor: '#fef08a' };  // yellow
        if (num === 4) return { ...base, backgroundColor: '#fecaca' };               // red
        return { ...base, backgroundColor: 'transparent' };
    };

    const tableThStyle: React.CSSProperties = { textAlign: 'left', padding: '12px 16px', fontSize: '14px', fontWeight: 500, color: '#374151', borderBottom: '1px solid #d1d5db', backgroundColor: '#f8fafc' };
    const tableTdBase: React.CSSProperties = { textAlign: 'left', padding: '12px 16px', fontSize: '14px', color: '#000000', backgroundColor: 'transparent' };

    const handleStartSession = async () => {
        console.log('Starting Quality Wall Record session...');

        try {
            // Store start data in localStorage
            const startData = {
                product: startFormData.productName.trim(),
                productName: startFormData.productName.trim(),
                batchNo: startFormData.batchNo.trim(),
                category: startFormData.category,
                lineNo: startFormData.lineNo.trim(),
                executiveName: startFormData.executiveName.trim(),
                sku: startFormData.sku.trim(),
                cr3ea_productname: startFormData.productName.trim(),
                cr3ea_batchno: startFormData.batchNo.trim(),
                cr3ea_category: startFormData.category,
                cr3ea_lineno: startFormData.lineNo.trim(),
                cr3ea_executivename: startFormData.executiveName.trim(),
                cr3ea_sku: startFormData.sku.trim()
            };

            const storageKey = `quality-wall-cycle-${currentCycle}-start-data`;
            localStorage.setItem(storageKey, JSON.stringify(startData));
            console.log('Start data stored in localStorage:', startData);

            // Set shift in sessionStorage
            try {
                sessionStorage.setItem('shiftValue', selectedCycle || 'Shift 1');
                console.log('Shift value set in sessionStorage:', selectedCycle || 'Shift 1');
            } catch (e) {
                console.warn('Unable to set shiftValue in sessionStorage', e);
            }

            setIsSessionStarted(true);
            setIsExpanded(prev => ({ ...prev, [currentCycle]: true }));
            console.log('Session started successfully');
        } catch (error) {
            console.error('Error starting session:', error);
            alert('Error starting session. Please try again.');
        }
    };

    const handleAddEvaluation = () => {
        const category = startFormData.category;
        setTabbedEvaluationEntries(prev => ({
            ...prev,
            [category]: [...(prev[category] ?? []), defaultEntry()]
        }));
    };

    const handleRemoveEvaluation = (id: string) => {
        const category = startFormData.category;
        const entries = tabbedEvaluationEntries[category] ?? [];
        if (entries.length > 1) {
            setTabbedEvaluationEntries(prev => ({
                ...prev,
                [category]: (prev[category] ?? []).filter(entry => entry.id !== id)
            }));
        } else {
            alert('At least one evaluation entry is required');
        }
    };

    const handleEvaluationChange = (id: string, field: string, value: string) => {
        const category = startFormData.category;
        setTabbedEvaluationEntries(prev => ({
            ...prev,
            [category]: (prev[category] ?? []).map(entry =>
                entry.id === id ? { ...entry, [field]: value } : entry
            )
        }));
    };

    const handleSaveSession = async () => {
        // Helper to filter non-empty entries per category
        const getNonEmptyEntries = (category: string, entries: EvaluationEntry[]): EvaluationEntry[] => {
            const isProduct = category === 'Product Quality';
            return entries.filter(entry =>
                isProduct
                    ? (
                        entry.membersPresent.trim() ||
                        entry.appearance.trim() ||
                        entry.color.trim() ||
                        entry.texture.trim() ||
                        entry.flavour.trim() ||
                        entry.taste.trim() ||
                        entry.remarksDefect.trim()
                    )
                    : (
                        entry.membersPresent.trim() ||
                        entry.packAppearance.trim() ||
                        entry.sealingQuality.trim() ||
                        entry.coding.trim()
                    )
            );
        };

        // Build list of categories (tabs) that have data
        const categoriesWithData: { category: string; entries: EvaluationEntry[] }[] =
            Object.entries(tabbedEvaluationEntries).map(([category, entries]) => {
                const nonEmpty = getNonEmptyEntries(category, entries || []);
                return { category, entries: nonEmpty };
            }).filter(item => item.entries.length > 0);

        if (categoriesWithData.length === 0) {
            alert('Please enter at least one evaluation entry before saving');
            return;
        }

        if (!plantTourId) {
            alert('Plant Tour ID is missing. Please start a plant tour first.');
            return;
        }

        try {
            console.log('Saving Quality Wall Record session...');
            console.log('Cycle:', currentCycle);
            console.log('Start Form Data:', startFormData);
            console.log('Evaluation Entries:', displayedEvaluationEntries);
            console.log('Is Offline:', isOfflineStarted);
            
            const userName = user?.Name || 'Current User';
            
            // Check if offline mode
            if (isOfflineStarted) {
                console.log('Saving Quality Wall Record data in offline mode to Redux...');
                
                // Build records for offline storage (ALL tabs that have data)
                const moment = (await import('moment')).default;
                const records: any[] = [];

                categoriesWithData.forEach(({ category, entries }) => {
                    const isProduct = category === 'Product Quality';
                    entries.forEach(entry => {
                        const base: any = {
                            cr3ea_qualitytourid: plantTourId || '',
                            cr3ea_title: 'Qualitywall_' + moment().format('DD/MM/YYYY'),
                            cr3ea_cycle: `Cycle-${currentCycle}`,
                            cr3ea_shift: sessionStorage.getItem("shiftValue") || selectedCycle || "shift 1",
                            cr3ea_observedby: userName || null,
                            cr3ea_productname: startFormData.productName || "N/A",
                            cr3ea_batchno: startFormData.batchNo || "N/A",
                            cr3ea_lineno: startFormData.lineNo || "N/A",
                            cr3ea_memberspresented: entry.membersPresent.trim() || "",
                            cr3ea_executivename: startFormData.executiveName || "N/A",
                            cr3ea_category: category,
                            cr3ea_sku: startFormData.sku || "",
                            cr3ea_rating: entry.rating || null,
                            cr3ea_platform: "PWA"
                        };
                        if (isProduct) {
                            base.cr3ea_coding = '';
                            base.cr3ea_packappearance = '';
                            base.cr3ea_sealingquality = '';
                            base.cr3ea_appearance = entry.appearance.trim() || null;
                            base.cr3ea_color = entry.color.trim() || null;
                            base.cr3ea_texture = entry.texture.trim() || null;
                            base.cr3ea_flavour = entry.flavour.trim() || null;
                            base.cr3ea_taste = entry.taste.trim() || null;
                            base.cr3ea_remarksdefect = entry.remarksDefect.trim() || null;
                        } else {
                            base.cr3ea_coding = entry.coding.trim() || "";
                            base.cr3ea_packappearance = entry.packAppearance.trim() || "";
                            base.cr3ea_sealingquality = entry.sealingQuality.trim() || "";
                        }
                        records.push(base);
                    });
                });
                
                // Create offline submission
                const offlineSubmission = {
                    cycleNo: currentCycle,
                    records: records,
                    timestamp: Date.now(),
                    plantTourId: plantTourId || ''
                };
                
                dispatch(addOfflineSubmissionByCategory({ 
                    category: 'Quality Wall Record', 
                    submission: offlineSubmission 
                }));
                
                // Save cycle data locally for display
                const savedCycleData = {
                    productName: startFormData.productName,
                    batchNo: startFormData.batchNo,
                    // Keep last selected category for backwards compatibility; per-row categories are stored below
                    category: startFormData.category,
                    lineNo: startFormData.lineNo,
                    executiveName: startFormData.executiveName,
                    sku: startFormData.sku,
                    evaluations: categoriesWithData.flatMap(({ category, entries }) =>
                        entries.map(entry => ({
                            membersPresent: entry.membersPresent,
                            packAppearance: entry.packAppearance,
                            sealingQuality: entry.sealingQuality,
                            coding: entry.coding,
                            rating: entry.rating || '',
                            appearance: entry.appearance,
                            color: entry.color,
                            texture: entry.texture,
                            flavour: entry.flavour,
                            taste: entry.taste,
                            remarksDefect: entry.remarksDefect,
                            category
                        }))
                    )
                };
                
                // Mark cycle as completed
                setCompletedCycles(prev => new Set([...prev, currentCycle]));
                setCycleData(prev => ({ ...prev, [currentCycle]: savedCycleData }));
                
                // Clear localStorage
                const storageKey = `quality-wall-cycle-${currentCycle}-start-data`;
                localStorage.removeItem(storageKey);
                
                const savedCycleNum = currentCycle;
                console.log('Quality Wall Record data saved to Redux. Total offline submissions:', (offlineSubmissionsByCategory['Quality Wall Record'] || []).length + 1);
                
                // Reset for next cycle
                setIsSessionStarted(false);
                setStartFormData({
                    productName: '',
                    batchNo: '',
                    category: 'Pack Quality',
                    lineNo: '',
                    executiveName: '',
                    sku: ''
                });
                setTabbedEvaluationEntries(initialTabbedEntries());
                
                // Move to next cycle
                const nextCycle = currentCycle + 1;
                setCurrentCycle(nextCycle);
                setIsExpanded(prev => ({ ...prev, [nextCycle]: true }));
                
                alert(`Cycle ${savedCycleNum} saved offline. Will sync when you cancel or sync offline mode.`);
                return; // Exit early for offline mode
            } else {
                // Online mode - save ALL tabs that have data
                let allRecordsToSave: QualityWallRecordData[] = [];

                for (const { category, entries } of categoriesWithData) {
                    const { savedData } = await collectEstimationDataCycleSave(
                        currentCycle,
                        { ...startFormData, category },
                        entries.map(entry => ({
                            membersPresent: entry.membersPresent,
                            packAppearance: entry.packAppearance,
                            sealingQuality: entry.sealingQuality,
                            coding: entry.coding,
                            rating: entry.rating || '',
                            appearance: entry.appearance,
                            color: entry.color,
                            texture: entry.texture,
                            flavour: entry.flavour,
                            taste: entry.taste,
                            remarksDefect: entry.remarksDefect
                        })),
                        plantTourId,
                        userName
                    );
                    allRecordsToSave = allRecordsToSave.concat(savedData);
                }
                
                // Save to API (all categories/rows together)
                const saveResult = await saveSectionApiCall(allRecordsToSave);
                
                if (!saveResult.success) {
                    throw new Error(saveResult.message || 'Failed to save data');
                }
                
                // Save cycle data locally for display
                const savedCycleData = {
                    productName: startFormData.productName,
                    batchNo: startFormData.batchNo,
                    // Keep last selected category for backwards compatibility; per-row categories are stored below
                    category: startFormData.category,
                    lineNo: startFormData.lineNo,
                    executiveName: startFormData.executiveName,
                    sku: startFormData.sku,
                    evaluations: categoriesWithData.flatMap(({ category, entries }) =>
                        entries.map(entry => ({
                            membersPresent: entry.membersPresent,
                            packAppearance: entry.packAppearance,
                            sealingQuality: entry.sealingQuality,
                            coding: entry.coding,
                            rating: entry.rating || '',
                            appearance: entry.appearance,
                            color: entry.color,
                            texture: entry.texture,
                            flavour: entry.flavour,
                            taste: entry.taste,
                            remarksDefect: entry.remarksDefect,
                            category
                        }))
                    )
                };
                
                // Mark cycle as completed
                setCompletedCycles(prev => new Set([...prev, currentCycle]));
                setCycleData(prev => ({ ...prev, [currentCycle]: savedCycleData }));
                
                // Clear localStorage
                const storageKey = `quality-wall-cycle-${currentCycle}-start-data`;
                localStorage.removeItem(storageKey);
                
                alert(`Cycle ${currentCycle} saved successfully!`);
                
                // Reset for next cycle (both tabs)
                setIsSessionStarted(false);
                setStartFormData({
                    productName: '',
                    batchNo: '',
                    category: 'Pack Quality',
                    lineNo: '',
                    executiveName: '',
                    sku: ''
                });
                setTabbedEvaluationEntries(initialTabbedEntries());
                
                // Move to next cycle
                const nextCycle = currentCycle + 1;
                setCurrentCycle(nextCycle);
                setIsExpanded(prev => ({ ...prev, [nextCycle]: true }));
                
                // Refresh cycle data from API
                await fetchAndDisplayCycles();
            }
        } catch (error) {
            console.error('Error saving session:', error);
            alert(error instanceof Error ? error.message : 'Error saving session. Please try again.');
        }
    };

    const handleCancel = () => {
        if (window.confirm('Are you sure you want to cancel? All unsaved data will be lost.')) {
            setIsSessionStarted(false);
            setTabbedEvaluationEntries(initialTabbedEntries());
        }
    };

    // Format date as DD/MM/YYYY
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const formattedDate = `${day}/${month}/${year}`;

    const toggleCompletedCycleExpansion = (cycleNum: string) => {
        setExpandedCompletedCycles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cycleNum)) {
                newSet.delete(cycleNum);
            } else {
                newSet.add(cycleNum);
            }
            return newSet;
        });
    };

    // Fetch and display cycles from API
    const fetchAndDisplayCycles = async () => {
        if (!plantTourId || isOfflineStarted) {
            return;
        }

        try {
            // setIsLoadingCycles(true);
            console.log('Fetching Quality Wall Record cycles...');
            const fetchedData = await fetchCycleData(plantTourId);
            console.log('Fetched cycles:', fetchedData);

            if (fetchedData && fetchedData.length > 0) {
                // Update completed cycles and cycle data
                const completedSet = new Set<number>();
                const cycleDataMap: { [key: number]: any } = {};

                fetchedData.forEach(cycle => {
                    const cycleNum = parseInt(cycle.cycleNum);
                    completedSet.add(cycleNum);

                    // Initialize entry for this cycle if it doesn't exist
                    if (!cycleDataMap[cycleNum]) {
                        cycleDataMap[cycleNum] = {
                            productName: cycle.product,
                            batchNo: cycle.batchNum,
                            category: cycle.category,
                            lineNo: cycle.lineNum,
                            executiveName: cycle.executiveName,
                            sku: cycle.sku,
                            evaluations: []
                        };
                    }

                    // Append all row summaries, preserving per-row category if present
                    const existing = cycleDataMap[cycleNum];
                    const newEvaluations = cycle.rowsSummary.map((row: any) => ({
                        membersPresent: row.membersPresent || '',
                        packAppearance: row.packAppearance || '',
                        sealingQuality: row.sealingQuality || '',
                        coding: row.coding || '',
                        rating: row.rating || '',
                        appearance: row.appearance || '',
                        color: row.color || '',
                        texture: row.texture || '',
                        flavour: row.flavour || '',
                        taste: row.taste || '',
                        remarksDefect: row.remarksDefect || '',
                        category: row.category || cycle.category || ''
                    }));

                    existing.evaluations = [...existing.evaluations, ...newEvaluations];
                    // Keep last category seen for backward compatibility
                    existing.category = cycle.category || existing.category;
                    cycleDataMap[cycleNum] = existing;
                });

                setCompletedCycles(completedSet);
                setCycleData(cycleDataMap);

                // Set current cycle to next available
                if (completedSet.size > 0) {
                    const maxCycle = Math.max(...Array.from(completedSet));
                    setCurrentCycle(maxCycle + 1);
                }
            }
        } catch (error) {
            console.error('Error fetching cycles:', error);
        } finally {
            // setIsLoadingCycles(false);
        }
    };

    // Fetch cycles on mount
    useEffect(() => {
        if (plantTourId && !isOfflineStarted) {
            fetchAndDisplayCycles();
        }
    }, [plantTourId, isOfflineStarted]);

    return (
        <DashboardLayout>
            {/* Header Section with Back Button and Plant Tour ID */}
            <div className="bg-white px-3 sm:px-4 md:px-6 py-3 sm:py-4 mb-4 sm:mb-6 border-b border-gray-200 w-full">
                <div className="flex items-center justify-between gap-2">
                    {/* Back Button */}
                    <button
                        onClick={() => {
                            if (window.history.length > 1) {
                                navigate(-1);
                            } else {
                                navigate('/home');
                            }
                        }}
                        className="flex items-center text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                    >
                        <span className="text-lg mr-1">&lt;</span>
                        <span className="font-medium text-sm sm:text-base">Back</span>
                    </button>

                    {/* Plant Tour ID */}
                    <div className="text-right min-w-0 flex-1">
                        <span className="text-gray-700 text-sm sm:text-base">Plant Tour ID: </span>
                        <span className="text-blue-600 font-medium text-sm sm:text-base break-all truncate" title={plantTourId || 'N/A'}>
                            {plantTourId || 'N/A'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="p-4 sm:p-6 md:p-8 space-y-6">
                {/* Header Bar */}
                <div className="bg-slate-50 rounded-xl border border-slate-200 px-4 sm:px-6 py-4 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                            <h1 className="text-lg sm:text-xl font-bold text-slate-800 tracking-tight">Quality Wall Record</h1>
                            <div className="flex items-center gap-2 sm:gap-3 text-sm">
                                <span className="text-slate-400">·</span>
                                <span className="bg-slate-200 text-slate-700 rounded-full px-3 py-1 font-medium">{selectedCycle || 'Shift 1'}</span>
                                <span className="text-slate-400">·</span>
                                <span className="text-slate-600">{formattedDate}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Completed Cycles List – each expanded cycle shows Summary (Pack Quality + Product Quality tables) */}
                {Array.from(completedCycles).sort((a, b) => a - b).map(cycleNum => {
                    const data = cycleData[cycleNum];
                    const isExpandedCycle = expandedCompletedCycles.has(cycleNum.toString());
                    const packEvaluations = (data?.evaluations || []).filter((e: any) => (e.category || data?.category) === 'Pack Quality');
                    const productEvaluations = (data?.evaluations || []).filter((e: any) => (e.category || data?.category) === 'Product Quality');
                    return (
                        <div key={cycleNum} className="w-full mb-4 rounded-xl border border-slate-200 overflow-hidden shadow-sm bg-white">
                            <div className="bg-slate-50 border-b border-slate-200 px-4 sm:px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-lg font-bold text-slate-800">Cycle {cycleNum}</h2>
                                    <button
                                        type="button"
                                        className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-200/60 transition-colors"
                                        onClick={() => toggleCompletedCycleExpansion(cycleNum.toString())}
                                        aria-label={isExpandedCycle ? 'Collapse' : 'Expand'}
                                    >
                                        <svg className={`w-5 h-5 transition-transform duration-200 ${isExpandedCycle ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            {isExpandedCycle && data && (
                                <div className="bg-white px-4 sm:px-6 py-5 space-y-6">
                                    {/* Section 1: Pack Quality */}
                                    <div>
                                        <h3 className="text-base font-bold text-slate-800 mb-4">Pack Quality</h3>
                                        <div className="overflow-x-auto">
                                            <table className="w-full" style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={tableThStyle}>Product</th>
                                                        <th style={tableThStyle}>Line No</th>
                                                        <th style={tableThStyle}>Pkd/Batch No</th>
                                                        <th style={tableThStyle}>Executive Name</th>
                                                        <th style={tableThStyle}>SKU</th>
                                                        <th style={tableThStyle}>Members Present</th>
                                                        <th style={tableThStyle}>Pack Appearance</th>
                                                        <th style={tableThStyle}>Sealing Quality</th>
                                                        <th style={tableThStyle}>Coding</th>
                                                        <th style={tableThStyle}>Rating</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {!packEvaluations.length ? (
                                                        <tr><td colSpan={10} style={{ ...tableTdBase, color: '#64748b' }}>No Pack Quality data</td></tr>
                                                    ) : packEvaluations.map((evaluation: any, index: number) => (
                                                        <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                            <td style={tableTdBase}>{data.productName || '-'}</td>
                                                            <td style={tableTdBase}>{data.lineNo || '-'}</td>
                                                            <td style={tableTdBase}>{data.batchNo || '-'}</td>
                                                            <td style={tableTdBase}>{data.executiveName || '-'}</td>
                                                            <td style={tableTdBase}>{data.sku || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.membersPresent || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.packAppearance || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.sealingQuality || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.coding || '-'}</td>
                                                            <td style={getRatingCellStyle(evaluation.rating)}>{evaluation.rating ?? '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    {/* Section 2: Product Quality (no Rating) */}
                                    <div>
                                        <h3 className="text-base font-bold text-slate-800 mb-4">Product Quality</h3>
                                        <div className="overflow-x-auto">
                                            <table className="w-full" style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={tableThStyle}>Product</th>
                                                        <th style={tableThStyle}>Line No</th>
                                                        <th style={tableThStyle}>Pkd/Batch No</th>
                                                        <th style={tableThStyle}>Executive Name</th>
                                                        <th style={tableThStyle}>SKU</th>
                                                        <th style={tableThStyle}>Members Present</th>
                                                        <th style={tableThStyle}>Appearance</th>
                                                        <th style={tableThStyle}>Color</th>
                                                        <th style={tableThStyle}>Texture</th>
                                                        <th style={tableThStyle}>Flavour</th>
                                                        <th style={tableThStyle}>Taste</th>
                                                        <th style={tableThStyle}>Remarks Defect</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {!productEvaluations.length ? (
                                                        <tr><td colSpan={12} style={{ ...tableTdBase, color: '#64748b' }}>No Product Quality data</td></tr>
                                                    ) : productEvaluations.map((evaluation: any, index: number) => (
                                                        <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                            <td style={tableTdBase}>{data.productName || '-'}</td>
                                                            <td style={tableTdBase}>{data.lineNo || '-'}</td>
                                                            <td style={tableTdBase}>{data.batchNo || '-'}</td>
                                                            <td style={tableTdBase}>{data.executiveName || '-'}</td>
                                                            <td style={tableTdBase}>{data.sku || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.membersPresent || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.appearance || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.color || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.texture || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.flavour || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.taste || '-'}</td>
                                                            <td style={tableTdBase}>{evaluation.remarksDefect || '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Main Content - Current Cycle Section */}
                <div className={`rounded-xl border w-full overflow-hidden ${
                    completedCycles.has(currentCycle) 
                        ? 'border-slate-200 bg-white shadow-md' 
                        : 'bg-white border-slate-200 shadow-md'
                }`}>
                    {/* Cycle Header with Dropdown Icon (only for non-completed cycles) */}
                    {!completedCycles.has(currentCycle) && (
                        <div className="px-4 sm:px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg sm:text-xl font-bold text-slate-800 tracking-tight">Cycle {currentCycle}</h2>
                                <button
                                    type="button"
                                    className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-200/60 transition-colors"
                                    onClick={() => setIsExpanded(prev => ({ ...prev, [currentCycle]: !prev[currentCycle] }))}
                                    aria-label={isExpanded[currentCycle] ? 'Collapse' : 'Expand'}
                                >
                                    <svg
                                        className={`w-5 h-5 transition-transform duration-200 ${isExpanded[currentCycle] ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Completed Cycle Display (when current cycle is completed) */}
                    {completedCycles.has(currentCycle) && cycleData[currentCycle] && (
                        <>
                            {/* Cycle Header */}
                            <div className="bg-slate-50 border-b border-slate-200 px-4 sm:px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-lg font-bold text-slate-800">Cycle {currentCycle}</h2>
                                    <button
                                        type="button"
                                        className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-200/60 transition-colors"
                                        onClick={() => setIsExpanded(prev => ({ ...prev, [currentCycle]: !prev[currentCycle] }))}
                                        aria-label={isExpanded[currentCycle] ? 'Collapse' : 'Expand'}
                                    >
                                        <svg className={`w-5 h-5 transition-transform duration-200 ${isExpanded[currentCycle] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            
                            {/* Cycle completed Summary: Section 1 Pack Quality + Section 2 Product Quality */}
                            {isExpanded[currentCycle] && cycleData[currentCycle] && (() => {
                                const data = cycleData[currentCycle];
                                const packEvaluations = (data.evaluations || []).filter((e: any) => (e.category || data.category) === 'Pack Quality');
                                const productEvaluations = (data.evaluations || []).filter((e: any) => (e.category || data.category) === 'Product Quality');
                                return (
                                    <div className="bg-white px-4 sm:px-6 py-5 space-y-6">
                                        {/* Section 1: Pack Quality */}
                                        <div>
                                            <h3 className="text-base font-bold text-slate-800 mb-4">Pack Quality</h3>
                                            <div className="overflow-x-auto">
                                                <table className="w-full" style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                    <thead>
                                                        <tr>
                                                            <th style={tableThStyle}>Product</th>
                                                            <th style={tableThStyle}>Line No</th>
                                                            <th style={tableThStyle}>Pkd/Batch No</th>
                                                            <th style={tableThStyle}>Executive Name</th>
                                                            <th style={tableThStyle}>SKU</th>
                                                            <th style={tableThStyle}>Members Present</th>
                                                            <th style={tableThStyle}>Pack Appearance</th>
                                                            <th style={tableThStyle}>Sealing Quality</th>
                                                            <th style={tableThStyle}>Coding</th>
                                                            <th style={tableThStyle}>Rating</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {!packEvaluations.length ? (
                                                            <tr><td colSpan={10} style={{ ...tableTdBase, color: '#64748b' }}>No Pack Quality data</td></tr>
                                                        ) : packEvaluations.map((evaluation: any, index: number) => (
                                                            <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td style={tableTdBase}>{data.productName || '-'}</td>
                                                                <td style={tableTdBase}>{data.lineNo || '-'}</td>
                                                                <td style={tableTdBase}>{data.batchNo || '-'}</td>
                                                                <td style={tableTdBase}>{data.executiveName || '-'}</td>
                                                                <td style={tableTdBase}>{data.sku || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.membersPresent || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.packAppearance || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.sealingQuality || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.coding || '-'}</td>
                                                                <td style={getRatingCellStyle(evaluation.rating)}>{evaluation.rating ?? '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        {/* Section 2: Product Quality (no Rating) */}
                                        <div>
                                            <h3 className="text-base font-bold text-slate-800 mb-4">Product Quality</h3>
                                            <div className="overflow-x-auto">
                                                <table className="w-full" style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                    <thead>
                                                        <tr>
                                                            <th style={tableThStyle}>Product</th>
                                                            <th style={tableThStyle}>Line No</th>
                                                            <th style={tableThStyle}>Pkd/Batch No</th>
                                                            <th style={tableThStyle}>Executive Name</th>
                                                            <th style={tableThStyle}>SKU</th>
                                                            <th style={tableThStyle}>Members Present</th>
                                                            <th style={tableThStyle}>Appearance</th>
                                                            <th style={tableThStyle}>Color</th>
                                                            <th style={tableThStyle}>Texture</th>
                                                            <th style={tableThStyle}>Flavour</th>
                                                            <th style={tableThStyle}>Taste</th>
                                                            <th style={tableThStyle}>Remarks Defect</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {!productEvaluations.length ? (
                                                            <tr><td colSpan={12} style={{ ...tableTdBase, color: '#64748b' }}>No Product Quality data</td></tr>
                                                        ) : productEvaluations.map((evaluation: any, index: number) => (
                                                            <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td style={tableTdBase}>{data.productName || '-'}</td>
                                                                <td style={tableTdBase}>{data.lineNo || '-'}</td>
                                                                <td style={tableTdBase}>{data.batchNo || '-'}</td>
                                                                <td style={tableTdBase}>{data.executiveName || '-'}</td>
                                                                <td style={tableTdBase}>{data.sku || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.membersPresent || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.appearance || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.color || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.texture || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.flavour || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.taste || '-'}</td>
                                                                <td style={tableTdBase}>{evaluation.remarksDefect || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </>
                    )}

                    {/* Start Session Form */}
                    {!isSessionStarted && !completedCycles.has(currentCycle) && (
                        <div className="p-4 sm:p-6 md:p-8 space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                                <div>
                                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Product Name</label>
                                    <input
                                        type="text"
                                        value={startFormData.productName}
                                        onChange={(e) => setStartFormData(prev => ({ ...prev, productName: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors"
                                        placeholder="Enter Product Name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Line No</label>
                                    <input
                                        type="text"
                                        value={startFormData.lineNo}
                                        onChange={(e) => setStartFormData(prev => ({ ...prev, lineNo: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors"
                                        placeholder="Enter Line No"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Pkd/Batch No</label>
                                    <input
                                        type="text"
                                        value={startFormData.batchNo}
                                        onChange={(e) => setStartFormData(prev => ({ ...prev, batchNo: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors"
                                        placeholder="Enter Pkd/Batch No"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Executive Name</label>
                                    <input
                                        type="text"
                                        value={startFormData.executiveName}
                                        onChange={(e) => setStartFormData(prev => ({ ...prev, executiveName: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors"
                                        placeholder="Enter Executive Name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">SKU</label>
                                    <input
                                        type="text"
                                        value={startFormData.sku}
                                        onChange={(e) => setStartFormData(prev => ({ ...prev, sku: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors"
                                        placeholder="Enter SKU"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end pt-2">
                                <button
                                    type="button"
                                    onClick={handleStartSession}
                                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium text-sm rounded-lg shadow-sm hover:shadow transition-all"
                                >
                                    Start Session
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Session Started Content - Save Section */}
                    {isSessionStarted && isExpanded[currentCycle] && !completedCycles.has(currentCycle) && (
                        <div className="p-4 sm:p-6 space-y-6">
                            {/* Product details summary */}
                            <div className="bg-blue-50/70 rounded-xl p-4 sm:p-5 border border-blue-100">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Product details</p>
                                    <button
                                        type="button"
                                        className="p-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-200/60 transition-colors"
                                        onClick={() => setIsExpanded(prev => ({ ...prev, [currentCycle]: !prev[currentCycle] }))}
                                        aria-label="Collapse"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
                                    <div><span className="text-slate-500 font-medium">Product</span><span className="ml-2 text-slate-800">{startFormData.productName || 'N/A'}</span></div>
                                    <div><span className="text-slate-500 font-medium">SKU</span><span className="ml-2 text-slate-800">{startFormData.sku || 'N/A'}</span></div>
                                    <div><span className="text-slate-500 font-medium">Line No</span><span className="ml-2 text-slate-800">{startFormData.lineNo || 'N/A'}</span></div>
                                    <div><span className="text-slate-500 font-medium">Pkd/Batch No</span><span className="ml-2 text-slate-800">{startFormData.batchNo || 'N/A'}</span></div>
                                    <div><span className="text-slate-500 font-medium">Executive Name</span><span className="ml-2 text-slate-800">{startFormData.executiveName || 'N/A'}</span></div>
                                    <div><span className="text-slate-500 font-medium">Category</span><span className="ml-2 text-slate-800">{startFormData.category || 'N/A'}</span></div>
                                </div>
                            </div>

                            {/* Tab-based Category selection */}
                            <div>
                                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Category</p>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setStartFormData(prev => ({ ...prev, category: 'Pack Quality' }))}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                            startFormData.category === 'Pack Quality'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-white text-slate-800 border border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                                        }`}
                                    >
                                        Pack Quality
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setStartFormData(prev => ({ ...prev, category: 'Product Quality' }))}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                            startFormData.category === 'Product Quality'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-white text-slate-800 border border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                                        }`}
                                    >
                                        Product Quality
                                    </button>
                                </div>
                            </div>

                            {/* Evaluation Entries (per category tab) */}
                            <div className="space-y-4">
                                {displayedEvaluationEntries.map((entry, index) => (
                                    <div key={entry.id} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all">
                                        <div className="flex items-center justify-between mb-4">
                                            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Evaluation {index + 1}</h4>
                                            {displayedEvaluationEntries.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveEvaluation(entry.id)}
                                                    className="p-1.5 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                                                    aria-label="Remove evaluation"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                        {startFormData.category === 'Product Quality' ? (
                                            <>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Members Present</label>
                                                        <input type="text" value={entry.membersPresent} onChange={(e) => handleEvaluationChange(entry.id, 'membersPresent', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Appearance</label>
                                                        <input type="text" value={entry.appearance} onChange={(e) => handleEvaluationChange(entry.id, 'appearance', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Color</label>
                                                        <input type="text" value={entry.color} onChange={(e) => handleEvaluationChange(entry.id, 'color', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Texture</label>
                                                        <input type="text" value={entry.texture} onChange={(e) => handleEvaluationChange(entry.id, 'texture', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Flavour</label>
                                                        <input type="text" value={entry.flavour} onChange={(e) => handleEvaluationChange(entry.id, 'flavour', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Taste</label>
                                                        <input type="text" value={entry.taste} onChange={(e) => handleEvaluationChange(entry.id, 'taste', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Remarks Defect</label>
                                                        <input type="text" value={entry.remarksDefect} onChange={(e) => handleEvaluationChange(entry.id, 'remarksDefect', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Members Present</label>
                                                        <input type="text" value={entry.membersPresent} onChange={(e) => handleEvaluationChange(entry.id, 'membersPresent', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Pack Appearance</label>
                                                        <input type="text" value={entry.packAppearance} onChange={(e) => handleEvaluationChange(entry.id, 'packAppearance', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Sealing Quality</label>
                                                        <input type="text" value={entry.sealingQuality} onChange={(e) => handleEvaluationChange(entry.id, 'sealingQuality', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Coding</label>
                                                        <input type="text" value={entry.coding} onChange={(e) => handleEvaluationChange(entry.id, 'coding', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors" placeholder="Enter value" />
                                                    </div>
                                                </div>
                                                <div className="mt-4 max-w-xs">
                                                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Rating</label>
                                                    <select value={entry.rating} onChange={(e) => handleEvaluationChange(entry.id, 'rating', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors">
                                                        <option value="">Select</option>
                                                        {[0, 1, 2, 3, 4].map((n) => (<option key={n} value={String(n)}>{n}</option>))}
                                                    </select>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-slate-200">
                                <button
                                    type="button"
                                    onClick={handleAddEvaluation}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-colors text-sm font-medium"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    Add
                                </button>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="px-5 py-2.5 bg-white border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 active:bg-blue-100 transition-colors text-sm font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveSession}
                                        className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 shadow-sm hover:shadow transition-all text-sm font-medium"
                                    >
                                        Save Session
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Next Cycle (Disabled) */}
                {!completedCycles.has(currentCycle + 1) && (
                    <div className="bg-gray-100 rounded-lg shadow-sm border border-gray-300 p-3 sm:p-4 md:p-6 w-full opacity-60">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <h2 className="text-base sm:text-lg font-bold text-gray-500">Cycle {currentCycle + 1}</h2>
                            </div>
                            <svg
                                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 cursor-not-allowed"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default QualityWallRecord;

